import { useCallback, useRef, useState } from 'react';
import { useCampaignBuilderV2Api } from '../api';

/** Only what the 422 check reads — the modal names no words and shows no detail. */
interface BlocklistRejection {
  code?: number;
  error?: string;
}

/**
 * The shared BannedWordsModal names no words — it says restricted content was
 * found and offers Continue Anyways / Edit Message. So only the verdict is kept
 * here; collecting the matched words would be state nothing can render.
 */
export function useBlocklistGuard() {
  const api = useCampaignBuilderV2Api();
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  // Stored callback to invoke when user clicks "Continue Anyways"
  const pendingForceSubmitRef = useRef<(() => void) | null>(null);

  const confirm = useCallback(() => {
    setModalOpen(false);
    pendingForceSubmitRef.current?.();
    pendingForceSubmitRef.current = null;
  }, []);

  const check = useCallback(async (
    texts: string[],
    onBypass?: () => void,
  ): Promise<boolean> => {
    const filtered = texts.filter(Boolean);
    if (filtered.length === 0) return true;

    // Tracked separately from the match list: a hit reported as `hasMatches`
    // with no words attached still has to stop the save.
    let blocked = false;
    try {
      const res = await api.validateBlocklist({ texts: filtered });
      const data = res?.data;
      if (data?.hasMatches || (data?.matches?.length ?? 0) > 0) {
        blocked = true;
      }
    } catch (e) {
      const err = e as BlocklistRejection;
      const is422 = err?.code === 422 || err?.error === 'blocklist_match';
      if (is422) {
        blocked = true;
      } else {
        throw err;
      }
    }

    if (blocked) {
      pendingForceSubmitRef.current = onBypass ?? null;
      setModalOpen(true);
      return false;
    }
    return true;
  }, [api]);

  return { modalOpen, closeModal, confirm, check };
}
